import React, { useEffect, useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileCTA from '@/components/MobileCTA';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Link } from 'react-router-dom';
import { Waves, Shovel, Building, Shield, CheckCircle, Star, ArrowRight, Phone, Truck, Palette } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import SEO from '@/components/SEO';
import ExpandedFAQSection from '@/components/ExpandedFAQSection';
import RelatedMaterialsLinks from '@/components/RelatedMaterialsLinks';
import { sandFaqs } from '@/data/productFaqs';
import torpedoSand from '@/assets/torpedo-sand.webp';

interface Product {
  id: string;
  name_en: string;
  name_es: string;
  description_en: string | null;
  description_es: string | null;
  features_en: string[] | null;
  features_es: string[] | null;
  price_book: Array<{
    list_price: number;
    sale_price: number | null;
    unit_label_en: string;
    unit_label_es: string;
  }>;
}

// Static sand varieties data - products we have on hand
const sandVarieties = [
  {
    id: 'masonry-sand',
    name: 'Masonry Sand',
    nameEs: 'Arena de Mampostería',
    price: 42,
    image: torpedoSand,
    description: 'Fine mason sand perfect for construction and landscaping projects',
    descriptionEs: 'Arena mason fina perfecta para proyectos de construcción y jardinería',
    features: ['Fine Texture', 'Construction Grade', 'Clean Washed', 'Versatile Use'],
    featuresEs: ['Textura Fina', 'Grado Construcción', 'Lavado Limpio', 'Uso Versátil'],
    coverage: 'Standard coverage per cubic yard',
    applications: ['Masonry work', 'Paver base', 'Concrete mixing', 'General construction'],
    applicationsEs: ['Trabajo de mampostería', 'Base de adoquines', 'Mezcla de concreto', 'Construcción general'],
    availability: 'In Stock'
  },
  {
    id: 'torpedo-sand',
    name: 'Torpedo Sand',
    nameEs: 'Arena Torpedo',
    price: 59,
    image: torpedoSand,
    description: 'Coarse torpedo sand ideal for drainage and construction applications',
    descriptionEs: 'Arena torpedo gruesa ideal para aplicaciones de drenaje y construcción',
    features: ['Coarse Texture', 'Drainage Grade', 'Construction Quality', 'Clean Washed'],
    featuresEs: ['Textura Gruesa', 'Grado Drenaje', 'Calidad Construcción', 'Lavado Limpio'],
    coverage: 'Standard coverage per cubic yard',
    applications: ['French drains', 'Septic systems', 'Under concrete slabs', 'Heavy duty drainage'],
    applicationsEs: ['Drenajes franceses', 'Sistemas sépticos', 'Bajo losas de concreto', 'Drenaje pesado'],
    availability: 'In Stock'
  }
];


const SandPage = () => {
  const { language } = useLanguage();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSandProducts = async () => {
      try {
        // First get the sand category ID
        const { data: category } = await supabase
          .from('categories')
          .select('id')
          .eq(`slug_${language}`, language === 'en' ? 'sand' : 'arena')
          .single();

        if (category) {
          const { data: sandProducts } = await supabase
            .from('products')
            .select(`
              *,
              price_book (
                list_price,
                sale_price,
                unit_label_en,
                unit_label_es
              )
            `)
            .eq('category_id', category.id)
            .eq('is_active', true)
            .order('sort_order');

          setProducts(sandProducts || []);
        }
      } catch (error) {
        console.error('Error fetching sand products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSandProducts();
  }, [language]);

  const getProductName = (product: Product) => {
    return language === 'en' ? product.name_en : product.name_es;
  };

  const getProductDescription = (product: Product) => {
    return language === 'en' ? product.description_en : product.description_es;
  };

  const getProductFeatures = (product: Product) => {
    return language === 'en' ? product.features_en : product.features_es;
  };

  const getProductPrice = (product: Product) => {
    const priceInfo = product.price_book?.[0];
    if (!priceInfo) return '$0';
    const price = priceInfo.sale_price || priceInfo.list_price;
    return `$${price}`;
  };

  const getUnitLabel = (product: Product) => {
    const priceInfo = product.price_book?.[0];
    if (!priceInfo) return '';
    return language === 'en' ? priceInfo.unit_label_en : priceInfo.unit_label_es;
  };

  const seoTitle = language === 'en' 
    ? 'Premium Sand Delivery Texas | Masonry & Torpedo Sand'
    : 'Entrega de Arena Premium Texas | Arena de Mampostería y Torpedo';
  
  const seoDescription = language === 'en'
    ? 'Quality sand delivery in Montgomery County TX. Masonry sand & torpedo sand in stock. Other sand types available upon request. Professional delivery service.'
    : 'Entrega de arena de calidad en el Condado Montgomery TX. Arena de mampostería y torpedo en stock. Otros tipos de arena disponibles bajo pedido.';
  
  const seoKeywords = language === 'en'
    ? 'sand delivery texas, masonry sand montgomery county, torpedo sand conroe, construction sand woodlands, drainage sand texas, sand delivery service'
    : 'entrega arena texas, arena mampostería montgomery county, arena torpedo conroe, arena construcción woodlands, arena drenaje texas, servicio entrega arena';

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": seoTitle,
    "description": seoDescription,
    "category": "Landscaping Supplies",
    "offers": {
      "@type": "AggregateOffer",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock"
    },
    "provider": {
      "@type": "LocalBusiness",
      "name": "The Grass Depot LLC",
      "address": {
        "@type": "PostalAddress",
        "addressRegion": "TX",
        "addressLocality": "Montgomery County"
      }
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonical={language === 'en' ? '/en/sand' : '/es/arena'}
        structuredData={structuredData}
        language={language}
        breadcrumbs={[
          { name: language === 'en' ? 'Home' : 'Inicio', url: `/${language}` },
          { name: language === 'en' ? 'Products' : 'Productos', url: language === 'en' ? '/en/products' : '/es/productos' },
          { name: language === 'en' ? 'Sand' : 'Arena', url: language === 'en' ? '/en/sand' : '/es/arena' },
        ]}
      />
      <Header />
      
      <main>
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-amber-500 to-orange-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <div className="flex items-center justify-center mb-6">
            <Waves className="h-12 w-12 mr-4" />
            <h1 className="text-4xl lg:text-5xl font-bold">
              {language === 'en' ? 'Premium Sand Materials' : 'Materiales de Arena Premium'}
            </h1>
          </div>
            <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
              {language === 'en'
                ? 'Premium masonry and torpedo sand available on hand. Professional delivery throughout Montgomery County. Other sand types available upon request.'
                : 'Arena de mampostería y torpedo premium disponible en stock. Entrega profesional en todo el Condado Montgomery. Otros tipos de arena disponibles bajo pedido.'
              }
            </p>
            <div className="flex flex-wrap justify-center gap-4 mt-8">
              <Badge variant="secondary" className="bg-white/20 text-white border-white/30 px-4 py-2 text-base">
                <CheckCircle className="h-5 w-5 mr-2" />
                {language === 'en' ? '📦 In Stock Daily' : '📦 En Stock Diariamente'}
              </Badge>
              <Badge variant="secondary" className="bg-white/20 text-white border-white/30 px-4 py-2 text-base">
                <Truck className="h-5 w-5 mr-2" />
                {language === 'en' ? '🚛 Montgomery County Delivery' : '🚛 Entrega Condado Montgomery'}
              </Badge>
            </div>
        </div>
      </section>

      {/* Applications Section */}
      <section className="py-12 bg-muted">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold mb-4 text-foreground">
              {language === 'en' ? 'Sand Applications' : 'Aplicaciones de Arena'}
            </h2>
          </div>
          
          <div className="grid md:grid-cols-4 gap-6">
            {[
              {
                icon: Shield,
                title: language === 'en' ? 'Playgrounds' : 'Parques Infantiles',
                description: language === 'en' 
                  ? 'Safe, clean play sand for children\'s play areas'
                  : 'Arena de juego segura y limpia para áreas de juego infantiles'
              },
              {
                icon: Building,
                title: language === 'en' ? 'Construction' : 'Construcción',
                description: language === 'en'
                  ? 'High-quality sand for concrete and construction projects'
                  : 'Arena de alta calidad para proyectos de concreto y construcción'
              },
              {
                icon: Waves,
                title: language === 'en' ? 'Drainage' : 'Drenaje',
                description: language === 'en'
                  ? 'Effective drainage solutions for landscaping'
                  : 'Soluciones de drenaje efectivas para jardinería'
              },
              {
                icon: Shovel,
                title: language === 'en' ? 'Leveling' : 'Nivelación',
                description: language === 'en'
                  ? 'Perfect for leveling and base preparation'
                  : 'Perfecto para nivelación y preparación de bases'
              }
            ].map((application, index) => (
              <Card key={index} className="p-6 text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <application.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold mb-2 text-foreground">{application.title}</h3>
                <p className="text-sm text-muted-foreground">{application.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Sand Products Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              {language === 'en' ? 'Sand Products On Hand' : 'Productos de Arena en Stock'}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {language === 'en'
                ? 'High-quality sand materials ready for immediate delivery. Additional sand types available upon request.'
                : 'Materiales de arena de alta calidad listos para entrega inmediata. Tipos adicionales de arena disponibles bajo pedido.'
              }
            </p>
          </div>

          <Tabs defaultValue="grid" className="w-full">
            <TabsList className="grid w-full grid-cols-2 max-w-md mx-auto mb-12">
              <TabsTrigger value="grid">
                {language === 'en' ? 'View Products' : 'Ver Productos'}
              </TabsTrigger>
              <TabsTrigger value="custom">
                {language === 'en' ? 'Custom Orders' : 'Pedidos Personalizados'}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="grid">
              <div className="grid md:grid-cols-2 gap-8">
                {sandVarieties.map((sand, index) => (
                  <Card 
                    key={sand.id} 
                    className="group hover:shadow-glow transition-all duration-500 border-2 hover:border-primary overflow-hidden animate-fade-in"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={sand.image} 
                        alt={language === 'en' ? sand.name : sand.nameEs}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                      <div className="absolute top-4 right-4 bg-white rounded-lg px-3 py-1 shadow-lg">
                        <span className="font-bold text-primary">
                          ${sand.price}/yard
                        </span>
                      </div>
                      <div className="absolute top-4 left-4 bg-green-500 text-white rounded-lg px-3 py-1 shadow-lg">
                        <span className="font-semibold text-sm">
                          {language === 'en' ? 'In Stock' : 'En Stock'}
                        </span>
                      </div>
                      <div className="absolute bottom-4 left-4">
                        <h3 className="text-xl font-bold text-white mb-1">
                          {language === 'en' ? sand.name : sand.nameEs}
                        </h3>
                      </div>
                    </div>
                    
                    <CardContent className="p-6">
                      <p className="text-muted-foreground mb-4 text-sm">
                        {language === 'en' ? sand.description : sand.descriptionEs}
                      </p>
                      
                      {/* Features */}
                      <div className="grid grid-cols-2 gap-2 mb-4">
                        {(language === 'en' ? sand.features : sand.featuresEs).slice(0, 4).map((feature, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <CheckCircle className="h-3 w-3 text-primary flex-shrink-0" />
                            <span className="text-xs text-muted-foreground">{feature}</span>
                          </div>
                        ))}
                      </div>

                      {/* Coverage */}
                      <div className="mb-4 p-3 bg-muted/30 rounded-lg text-xs">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            {language === 'en' ? 'Coverage:' : 'Cobertura:'}
                          </span>
                          <span className="font-medium text-right">{sand.coverage}</span>
                        </div>
                      </div>

                      {/* Applications */}
                      <div className="mb-4">
                        <h4 className="font-semibold text-foreground mb-2 text-sm">
                          {language === 'en' ? 'Best For:' : 'Mejor Para:'}
                        </h4>
                        <div className="flex flex-wrap gap-1">
                          {(language === 'en' ? sand.applications : sand.applicationsEs).slice(0, 3).map((app, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">
                              {app}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        <Link to={`/${language}/${language === 'en' ? 'quote' : 'cotizar'}`} className="flex-1">
                          <Button variant="hero" className="w-full group-hover:scale-105 transition-transform">
                            {language === 'en' ? 'Add to Quote' : 'Agregar a Cotización'}
                          </Button>
                        </Link>
                        <a href="tel:9363200763">
                          <Button variant="outline" size="icon">
                            <Phone className="h-4 w-4" />
                          </Button>
                        </a>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="custom">
              <Card className="max-w-4xl mx-auto">
                <CardHeader className="text-center">
                  <CardTitle className="text-2xl font-bold text-foreground">
                    {language === 'en' ? 'Custom Sand Orders' : 'Pedidos Personalizados de Arena'}
                  </CardTitle>
                  <p className="text-muted-foreground">
                    {language === 'en'
                      ? 'Need a specific type of sand? We can source various sand types upon request.'
                      : '¿Necesita un tipo específico de arena? Podemos conseguir varios tipos de arena bajo pedido.'
                    }
                  </p>
                </CardHeader>
                <CardContent className="p-8">
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <h3 className="font-semibold text-foreground mb-4">
                        {language === 'en' ? 'Available Upon Request:' : 'Disponible Bajo Pedido:'}
                      </h3>
                      <ul className="space-y-2 text-sm text-muted-foreground">
                        <li className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-primary" />
                          {language === 'en' ? 'Play Sand (certified safe)' : 'Arena de Juego (certificada segura)'}
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-primary" />
                          {language === 'en' ? 'Concrete Sand' : 'Arena para Concreto'}
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-primary" />
                          {language === 'en' ? 'Washed River Sand' : 'Arena de Río Lavada'}
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-primary" />
                          {language === 'en' ? 'Leveling Sand' : 'Arena de Nivelación'}
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-primary" />
                          {language === 'en' ? 'Specialty Construction Sands' : 'Arenas Especiales de Construcción'}
                        </li>
                      </ul>
                    </div>
                    <div className="space-y-4">
                      <div className="p-4 bg-primary/5 rounded-lg">
                        <h4 className="font-semibold text-primary mb-2">
                          {language === 'en' ? 'How to Order:' : 'Cómo Pedir:'}
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          {language === 'en'
                            ? 'Call us with your specific sand requirements. We\'ll source the material and provide delivery timing and pricing.'
                            : 'Llámenos con sus requisitos específicos de arena. Conseguiremos el material y proporcionaremos el tiempo de entrega y precios.'
                          }
                        </p>
                      </div>
                      <div className="flex gap-3">
                        <a href="tel:9363200763" className="flex-1">
                          <Button variant="hero" className="w-full">
                            <Phone className="mr-2 h-4 w-4" />
                            {language === 'en' ? 'Call for Custom Order' : 'Llamar para Pedido Personalizado'}
                          </Button>
                        </a>
                        <Link to={`/${language}/${language === 'en' ? 'quote' : 'cotizar'}`}>
                          <Button variant="outline">
                            {language === 'en' ? 'Quote' : 'Cotización'}
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>
      <ExpandedFAQSection faqs={sandFaqs} language={language as 'en' | 'es'} />
      <RelatedMaterialsLinks language={language as 'en' | 'es'} exclude={['sand']} />
      </main>
      
      <Footer />
      <MobileCTA />
    </div>
  );
};

export default SandPage;